// Enums
export const ClaimStatus = {
  TO_BE_COMPLETED: 'TO_BE_COMPLETED',
  SUBMITTED: 'SUBMITTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  IN_REVISION: 'IN_REVISION',
  REVISION_COMPLETED: 'REVISION_COMPLETED',
  NEW: 'NEW',
  DRAFT: 'DRAFT',
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  INFORMATION_REQUESTED: 'INFORMATION_REQUESTED',
} as const;

export type ClaimStatus = typeof ClaimStatus[keyof typeof ClaimStatus];

export const Gender = {
  MALE: 'M',
  FEMALE: 'F',
  OTHER: 'X'
} as const;

export type Gender = typeof Gender[keyof typeof Gender];

export const ExpenseCategory = {
  MEDICAL: 'medical',
  CANCELLATION: 'cancellation',
  LUGGAGE: 'luggage',
  PHARMACEUTICAL: 'pharmaceutical',
  DENTAL: 'dental'
} as const;

export type ExpenseCategory = typeof ExpenseCategory[keyof typeof ExpenseCategory];

export const RequestType = {
  NEW_CLAIM: 'new_claim',
  ADD_EXPENSES: 'add_expenses',
  CONTINUE_DRAFT: 'continue_draft'
} as const;

export type RequestType = typeof RequestType[keyof typeof RequestType];

export const TravelPurpose = {
  IMMIGRATION: 'IMMIGRATION',
  MISSING_FROM_FORM: 'MISSING_FROM_FORM',
  OTHER: 'OTHER',
  RELOCATION: 'RELOCATION',
  SEEK_MEDICAL_CARE: 'SEEK_MEDICAL_CARE',
  STUDY: 'STUDY',
  TEMPORARY_WORK: 'TEMPORARY_WORK',
  VACATION: 'VACATION',
  WORK: 'WORK'
} as const;

export type TravelPurpose = typeof TravelPurpose[keyof typeof TravelPurpose];

export const InsuranceType = {
  EMPLOYER: 'employer',
  TRAVEL: 'travel',
  CREDIT_CARD: 'credit_card',
  NONE: 'none'
} as const;

export type InsuranceType = typeof InsuranceType[keyof typeof InsuranceType];

export const ClaimType = {
  MEDICAL: 'medical',
  CANCELLATION: 'cancellation',
  BAGGAGE: 'baggage',
  COMBINATION: 'combination'
} as const;

export type ClaimType = typeof ClaimType[keyof typeof ClaimType];

export const EventType = {
  ILLNESS: 'illness',
  ACCIDENT: 'accident',
  EMERGENCY: 'emergency',
  THEFT: 'theft',
  OTHER: 'other'
} as const;

export type EventType = typeof EventType[keyof typeof EventType];

export const AccidentType = {
  ROAD: 'road',
  WORK: 'work',
  THIRD_PARTY: 'third_party',
  OTHER: 'other'
} as const;

export type AccidentType = typeof AccidentType[keyof typeof AccidentType];

export const ReimbursementMode = {
  DIRECT_DEPOSIT: 'direct_deposit',
  CHECK: 'check',
  DIRECT_PAYMENT: 'direct_payment'
} as const;

export type ReimbursementMode = typeof ReimbursementMode[keyof typeof ReimbursementMode];

export const Province = {
  ON: 'ON',
  QC: 'QC',
  BC: 'BC',
  AB: 'AB',
  SK: 'SK',
  MB: 'MB',
  NB: 'NB',
  NS: 'NS',
  PE: 'PE',
  NL: 'NL',
  NT: 'NT',
  NU: 'NU',
  YT: 'YT'
} as const;

export type Province = typeof Province[keyof typeof Province];

// Base interfaces
export interface BaseEntity {
  id: string;
  createdAt: Date;
  updatedAt: Date;
}

// Contract Type Enum
export const ContractType = {
  VISITOR : 'VISITOR',           // Visiteur - Travel insurance for visitors to Canada
  SINGLE_TRIP : 'SINGLE_TRIP',   // Voyage Simple - Single trip travel insurance
  MULTI_TRIP : 'MULTI_TRIP',      // Voyages Multiples - Multiple trips travel insurance
  TOP_UP : 'TOP_UP',
  EXTENSION : 'EXTENSION'
} as const;

export type ContractType = typeof ContractType[keyof typeof ContractType];

// Contract Relationship type
export const ContractRole = {
    POLICY_HOLDER: 'POLICY_HOLDER',
    SPOUSE: 'SPOUSE',
    DEPENDENT_CHILD: 'DEPENDENT_CHILD',
    GROUP_MEMBER: 'GROUP_MEMBER'
} as const;

export type ContractRole = typeof ContractRole[keyof typeof ContractRole];

// User and Contract interfaces
export interface User {
  id: string;
  fullName: string;
  email: string;
  language: 'FR' | 'EN';
  contracts: Contract[];
}

export interface Contract {
  id: string;
  number: string;
  type: ContractType | string; // Allow string for backward compatibility
  validFrom: Date;
  validTo: Date;
  benefits: ContractBenefit[];
  beneficiaries: Beneficiary[];
  isActive: boolean;
}

export interface ContractBenefit {
  type: string;
  description: string;
  limit: number;
  currency: string;
  deductible?: number;
}

// Claim related interfaces
export interface Claim extends BaseEntity {
  caseNumber?: string;
  contractNumber: string;
  // DEV-7699: policyholderID removed. It only ever mirrored the parent context's
  // clientId — which identifies the CONNECTED USER, not the policy holder — was
  // written verbatim into Portal_Data__c and read back on resume, and no consumer
  // ever read it. clientId now lives on parentContext and serves observability only.
  status: ClaimStatus;
  currentScreenCode?: string;
  totalAmountCAD: number;
  rtaEligible: boolean;
  submittedAt?: Date;
  expiresAt: Date;
  version: number;
  
  // Relations
  beneficiaries: Beneficiary[];
  expenses: Expense[];
  documents: Document[];
  travelInfo?: TravelInformation;
  medicalEvents: MedicalEvent[];
  workflow: WorkflowState;
}

export interface Beneficiary extends BaseEntity {
  claimId: string;
  fullName: string;
  dateOfBirth: Date;
  gender: Gender;
  province: Province;
  healthCardNumber: string;
  contractRole: ContractRole;
}

export interface TravelInformation extends BaseEntity {
  claimId: string;
  destination: string;
  travelPurpose: TravelPurpose;
  departureDate: Date;
  returnDate: Date;
  tripDuration: number;
  coverageValidated: boolean;
}

export interface MedicalEvent extends BaseEntity {
  claimId: string;
  eventType: EventType;
  eventDate: Date;
  eventDescription: string;
  medicalServicesDescription: string;
  healthcareProvider?: string;
  preExistingCondition: boolean;
  accidentType?: AccidentType;
}

export interface Expense extends BaseEntity {
  claimId: string;
  serviceProvider: string;
  serviceDate: Date;
  description: string;
  originalAmount: number;
  originalCurrency: string;
  exchangeRate: number;
  amountCAD: number;
  category: ExpenseCategory;
  aiExtracted: boolean;
  aiConfidence?: number;
}

export interface Document extends BaseEntity {
  claimId: string;
  type: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  sharePointUrl?: string;
  aiAnalyzed: boolean;
  aiResults?: any;
  uploadedAt: Date;
}

// DEV-6896 — Documents tab metadata sourced from NOVA Submitted_File form_object
// (kept distinct from the legacy `Document` interface above which has a different shape
// and is consumed elsewhere). The frontend never sees the `FORM` category — it is
// filtered server-side by the BFF (NovaToPortalMapper).
export type DocumentCategory =
  | 'INVOICE'
  | 'VOID_CHEQUE'
  | 'SUPPORTING_DOCUMENT'
  | 'OTHER';
// FORM intentionally absent — the FE never renders claim forms. The BFF filters them
// out server-side (NovaToPortalMapper.mapDocumentsFromSubmittedFiles), so they never
// reach the wire.

export interface SubmittedFileEntry {
  documentKey: string;
  name: string;
  type: string;
  uploadedAt: string;       // ISO 8601 — BFF resolves the Uploaded_At/Create_At fallback
  category: DocumentCategory;
  // createAt intentionally absent — BFF-internal (used only for uploadedAt fallback)
}

export interface WorkflowState {
  currentStep?: number; // Deprecated - kept for backward compatibility
  totalSteps?: number; // Deprecated - calculated dynamically based on flow type
  completedSteps?: number[]; // Deprecated - use stepData keys instead
  currentScreenCode?: string | null; // Current screen code
  stepData: { [screenCode: string]: any }; // Screen code based data
}

// Form interfaces
export interface FormStepProps {
  onNext: (data: any) => void;
  onPrevious: () => void;
  data: any;
  isValid?: boolean;
  isLoading?: boolean;
  errors?: { [key: string]: string };
}

export interface RequestTypeForm {
  type: RequestType;
  caseNumber?: string;
  draftId?: string;
}

export interface ContractSelectionForm {
  selectedContract: string;
  contractDetails?: Contract;
}

export interface OtherInsuranceForm {
  hasEmployerInsurance: boolean;
  hasOtherTravelInsurance: boolean;
  hasCreditCardInsurance: boolean;
  employerDetails?: string;
  travelDetails?: string;
  creditCardDetails?: string;
  claimAlreadySubmitted: boolean;
}

export interface BeneficiaryForm {
  selectedBeneficiaries: string[];
  beneficiaryDetails: {
    [id: string]: {
      gender: Gender;
      healthCardNumber: string;
    };
  };
}

export interface TravelConfirmationForm {
  destination: string;
  travelPurpose: TravelPurpose;
  departureDate: Date;
  returnDate: Date;
}

export interface ClaimTypeForm {
  selectedTypes: ClaimType[];
  typeDetails: {
    medical?: boolean;
    cancellation?: boolean;
    baggage?: boolean;
  };
}

export interface EventDetailsForm {
  eventType: EventType;
  eventDate: Date;
  eventDescription: string;
  eventLocation: string;
}

export interface ReimbursementModeForm {
  mode: ReimbursementMode;
  bankingDetails?: BankingInformation;
  addressDetails?: AddressInformation;
}

export interface BankingInformation {
  accountNumber: string;
  routingNumber: string;
  bankName: string;
  accountHolderName: string;
}

export interface AddressInformation {
  street: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
}

// API Response interfaces
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface EligibilityResult {
  eligible: boolean;
  rtaQualified: boolean;
  reason?: string;
  coveredAmount: number;
  deductible: number;
  limitations?: string[];
}

export interface ValidationResult {
  isValid: boolean;
  errors: { [field: string]: string };
}

export interface AIAnalysisResult {
  confidence: number;
  extractedData: {
    provider?: string;
    amount?: number;
    currency?: string;
    date?: Date;
    description?: string;
  };
  suggestions?: string[];
  errors?: string[];
}

// Component prop interfaces
export interface AlertProps {
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message?: string;
  children?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export interface FormFieldProps {
  id: string;
  label: string;
  type?: 'text' | 'email' | 'tel' | 'password' | 'number' | 'date' | 'select' | 'textarea';
  value: any;
  onChange: (value: any) => void;
  /**
   * Optional blur handler for the default (text) input, called with the current
   * value when the field loses focus. Used e.g. to normalize the NAM to
   * uppercase on blur without moving the caret while typing (DEV-7448).
   * Backward-compatible: other input types ignore it.
   */
  onBlur?: (value: string) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Rendered as-is under the field. Widened from `string` to `ReactNode` (DEV-7682) so a
   * help text can carry a clickable segment — see `components/EventDateReminderHelp.tsx`.
   * Only ever tested for truthiness and rendered, never string-manipulated.
   */
  helpText?: React.ReactNode;
  options?: { value: string; label: string }[];
  rows?: number;
  className?: string;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  maxLength?: number;
  /**
   * Opt-in flag for the date picker, used only by the date-of-birth field.
   * When true, the `type="date"` picker shows month/year dropdowns (navigable
   * back to 1900) and the text input is freely typeable while editing.
   * Defaults to false — all other date fields keep the existing behavior.
   */
  birthdate?: boolean;
}

export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  text?: string;
}

export interface ProgressIndicatorProps {
  currentStep: number;
  totalSteps: number;
  completedSteps?: number[];
  stepTitles?: string[];
  variant?: 'horizontal' | 'vertical';
}

// Store interfaces
export interface ClaimStore {
  // State
  currentClaim: Claim | null;
  isLoading: boolean;
  error: string | null;
  stepData: { [screenCode: string]: any };  // Screen code based
  parentContext: {
    contracts?: any[];
    clientId?: string;
    clientInfo?: any;
  } | null;
  currentScreenCode: string | null;
  deferClaimCreation: boolean;  // Defer claim creation until after 01-01-CONTRAT
  rtaEligible: boolean;
  drafts: { [id: string]: any };

  // BFF integration fields
  eventId: string | null; // Salesforce Event__c ID
  eventNumber: string | null; // Auto-generated event number
  submissionId: string | null; // Portal_Submission__c ID (null for legacy claims)
  referenceNumber: number | null; // Generated at event creation - shared across all documents
  existingEventNumber: string | null; // When linking to an existing event

  // Draft management
  draftsList: any[]; // UI-formatted list of drafts from API
  lastSaved: Date | null;
  eligibilityResult: any;

  // Actions
  initializeClaim: (type: RequestType, data?: any) => void;
  updateClaimData: (data: any, screenCode?: string) => void;
  /**
   * Drop a single screen's answers from stepData. Used when a conditional screen
   * becomes unreachable so its stale answers are never submitted (DEV-7479).
   */
  clearScreenData: (screenCode: string) => void;
  /**
   * Drop SOME keys of one screen and keep the rest (DEV-7682 ST7). `clearScreenData`
   * empties a whole screen and `updateClaimData` merges, so neither can remove a single
   * key — which is what an event-date change needs on `01-01-CONTRAT`: the eligibility
   * verdict computed at the OLD date goes, the contract itself stays. The key list belongs
   * to the caller (`ELIGIBILITY_DERIVED_CONTRACT_KEYS`), never to the store.
   */
  clearScreenDataKeys: (screenCode: string, keys: readonly string[]) => void;
  setCurrentScreenCode: (screenCode: string) => void;
  setDeferClaimCreation: (defer: boolean) => void;
  completeScreen: (screenCode: string) => void;
  saveDraft: () => Promise<void>;
  submitClaim: () => Promise<void>;
  loadDraft: (draftId: string) => Promise<void>;
  clearClaim: () => void;
  clearClaimFromSection: (sectionPrefix: string) => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  getDrafts: () => any[];
  fetchDrafts: () => Promise<void>;
  deleteDraft: (draftId: string) => void;
  initializeFromParentContext: (context: { contracts?: any[]; clientId?: string; clientInfo?: any }) => void;
  getParentContext: () => { contracts?: any[]; clientId?: string; clientInfo?: any } | null;
  getCompletedScreenCodes: () => string[];
  getCurrentClaimType: () => 'MEDICAL' | 'CANCELLATION' | 'BAGGAGE' | null;
  setExistingEventNumber: (eventNumber: string | null) => void;
  beginNewClaimOnExistingEvent: (eventNumber: string) => void;
  createNewClaimOnExistingEvent: () => Promise<boolean>;

  // Reference claim (generic read-only slice — used by flows acting on an existing claim)
  referenceClaim: ReferenceClaim | null;
  loadReferenceClaim: (eventId: string, submissionId?: string) => Promise<ReferenceClaim>;
  clearReferenceClaim: () => void;
}

export interface ReferenceClaim {
  eventId: string;
  eventNumber: string | null;
  submissionId: string | null;
  flowType: string | null;
  status: string | null;
  beneficiaryFullName: string | null;
  departureDate: string | null;
  returnDate: string | null;
  previousExpenses: any[];
  refundReceivedAmount: number;
  contractNumber: string | null;
  rawClaimData: Record<string, any> | null;
}

export interface UIStore {
  // UI State
  language: 'FR' | 'EN';
  theme: 'light' | 'dark';
  sidebarOpen: boolean;
  notifications: Notification[];

  // Navigation State
  currentFlow: 'MEDICAL' | 'CANCELLATION' | 'BAGGAGE' | null;
  currentScreen: string;
  completedScreens: string[];
  skipConditions: Record<string, boolean>;

  // UI Actions
  setLanguage: (language: 'FR' | 'EN') => void;
  setTheme: (theme: 'light' | 'dark') => void;
  toggleSidebar: () => void;
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;

  // Navigation Actions
  setFlow: (flow: 'MEDICAL' | 'CANCELLATION' | 'BAGGAGE') => void;
  setCurrentScreen: (screenCode: string) => void;
  completeScreen: (screenCode: string) => void;
  getNextScreenCode: () => string | null;
  getPreviousScreenCode: () => string | null;
  shouldSkipScreen: (screenCode: string) => boolean;
  setSkipConditions: (conditions: Record<string, boolean>) => void;
  getFlowProgressPercentage: () => number;
  resetNavigation: () => void;

  // Maintenance mode (DEV-6343)
  maintenance: boolean;
  maintenancePollIntervalMs: number;
  setMaintenance: (active: boolean, pollIntervalMs?: number) => void;
}

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number;
  persistent?: boolean;
}

// Utility types
export type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

export type StepComponent = React.ComponentType<FormStepProps>;

export type Translation = {
  [key: string]: string | Translation;
};

// Route types
export interface RouteDefinition {
  path: string;
  component: React.ComponentType<any>;
  exact?: boolean;
  title: string;
  step?: number;
}

// Configuration types
export interface AppConfig {
  apiBaseUrl: string;
  environment: 'development' | 'staging' | 'production';
  features: {
    rtaEnabled: boolean;
    aiAnalysisEnabled: boolean;
    draftSaveEnabled: boolean;
  };
  limits: {
    rtaMaxAmount: number;
    fileUploadMaxSize: number;
    maxFilesPerClaim: number;
    draftRetentionDays: number;
  };
}

// Mock data types for development
export interface MockData {
  users: User[];
  contracts: Contract[];
  claims: Claim[];
  exchanges: { [currency: string]: number };
}